/** Quantile on a sorted numeric array, p in [0, 1]. */
export function quantileSorted(sorted, p) {
  if (!sorted.length) return NaN;
  if (sorted.length === 1) return sorted[0];
  const pos = (sorted.length - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (pos - lo) * (sorted[hi] - sorted[lo]);
}

/**
 * Drop points whose numeric field is outside Tukey fences (IQR × factor).
 * Falls back to median ± k×MAD when IQR is degenerate.
 */
export function filterPointsByNumericOutliers(points, valueKey, { minPoints = 8, iqrFactor = 1.5 } = {}) {
  if (!Array.isArray(points) || points.length < minPoints) return points;
  const nums = points.map((p) => Number(p[valueKey])).filter((v) => Number.isFinite(v));
  if (nums.length < minPoints) return points;
  const sorted = [...nums].sort((a, b) => a - b);
  const q1 = quantileSorted(sorted, 0.25);
  const q3 = quantileSorted(sorted, 0.75);
  const iqr = q3 - q1;
  let low;
  let high;
  if (Number.isFinite(iqr) && iqr > 1e-12) {
    low = q1 - iqrFactor * iqr;
    high = q3 + iqrFactor * iqr;
  } else {
    const med = quantileSorted(sorted, 0.5);
    if (!Number.isFinite(med)) return points;
    const devs = nums.map((v) => Math.abs(v - med)).sort((a, b) => a - b);
    const mad = quantileSorted(devs, 0.5);
    if (!Number.isFinite(mad) || mad <= 1e-12) return points;
    const k = 6;
    low = med - k * mad;
    high = med + k * mad;
  }
  return points.filter((p) => {
    const v = Number(p[valueKey]);
    return Number.isFinite(v) && v >= low && v <= high;
  });
}

/** Apply outlier filter; if nothing left, return original (avoid empty chart). */
export function filterOutliersWithFallback(points, valueKey, options) {
  const next = filterPointsByNumericOutliers(points, valueKey, options);
  if (next.length === 0 && points.length > 0) return points;
  return next;
}
